import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { X, Send, Bot } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatWidget({ persona, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(
    () => `${persona.id}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    setMessages([{ role: "assistant", content: persona.greeting }]);
  }, [persona]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;

    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setInput("");
    setSending(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        persona: persona.id,
        session_id: sessionId,
        message: text,
      });
      setMessages((prev) => [...prev, { role: "assistant", content: res.data.reply }]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "I'm having a small connection hiccup. Please try again in a moment — or use the contact form to reach Melanie directly.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="chat-widget"
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-plum-deepest/60 backdrop-blur-sm p-0 md:p-6"
      onClick={onClose}
    >
      <div
        className="w-full md:max-w-md bg-cream rounded-t-2xl md:rounded-2xl shadow-2xl flex flex-col h-[85vh] md:h-[640px] overflow-hidden border border-plum/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-plum text-cream px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center border border-gold/40">
              <Bot size={20} className="text-gold" />
            </div>
            <div>
              <div className="font-display text-lg leading-none">{persona.name}</div>
              <div className="text-xs text-cream/70 mt-1 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                Online &middot; powered by AI
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="chat-close"
            className="text-cream/70 hover:text-cream transition-colors p-1"
            aria-label="Close chat"
          >
            <X size={22} />
          </button>
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          className="chat-scroll flex-1 overflow-y-auto px-4 py-5 flex flex-col gap-3 bg-cream-pale"
        >
          {messages.map((m, i) => (
            <div
              key={i}
              data-testid={`chat-message-${m.role}`}
              className={m.role === "user" ? "chat-bubble-user" : "chat-bubble-assistant"}
            >
              {m.content}
            </div>
          ))}
          {sending && (
            <div className="chat-bubble-assistant inline-flex items-center" data-testid="chat-typing">
              <span className="typing-dot" />
              <span className="typing-dot" />
              <span className="typing-dot" />
            </div>
          )}
        </div>

        {/* Demo notice */}
        <div className="px-4 py-2 bg-cream-warm/50 text-center text-[0.7rem] text-muted-plum tracking-wider uppercase border-t border-plum/5">
          Live AI demo &middot; sample business
        </div>

        {/* Input */}
        <form onSubmit={sendMessage} className="px-4 py-3 bg-cream border-t border-plum/10 flex items-center gap-2">
          <input
            data-testid="chat-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your message..."
            className="input-edit"
            disabled={sending}
          />
          <button
            type="submit"
            data-testid="chat-send"
            disabled={sending || !input.trim()}
            className="bg-plum text-cream rounded-full w-11 h-11 flex items-center justify-center hover:bg-plum-deep transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            aria-label="Send message"
          >
            <Send size={17} />
          </button>
        </form>
      </div>
    </div>
  );
}
